import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";
import { PICO_COLORS, getPicoTypeForTag, normalizePicoJson } from "../../../../utils/Picocolors";
import ScreeningAbstractPanel from "./ScreeningAbstractPanel";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import ScreeningPDFPanel from "./Screeningpdfpanel";

const AIResponsePanel = ({
    abstractId,
    fullTextScreeningId,
    slrFabricId,
    onNavigationUpdate,
    pico_json,
    activeFilters = {},
}) => {
    const navigate = useNavigate();

    const [aiData, setAiData] = useState(null);
    const [aiFullTextData, setAiFullTextData] = useState(null);
    const [loadingAbstract, setLoadingAbstract] = useState(false);
    const [loadingFullText, setLoadingFullText] = useState(false);
    const [selectedModelIndex, setSelectedModelIndex] = useState(0); // Track selected model
    const loading = loadingAbstract || loadingFullText;

    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [popupErrorMessage, setPopupErrorMessage] = useState("");

    const showError = (msg) => {
      setPopupErrorMessage(msg || "Something went wrong");
      setIsErrorPopupVisible(true);
    };

    const closeErrorPopup = () => setIsErrorPopupVisible(false);
   
    // useMemo for headers (Eavior pattern)
    const headers = useMemo(() => ({
        Authorization: GetCookie("access_token"),
    }), []);

    const effectiveAIData = fullTextScreeningId
        ? aiFullTextData
        : aiData;

    const actionNeeded = fullTextScreeningId
        ? effectiveAIData?.data?.document?.action_needed
        : effectiveAIData?.data?.study?.action_needed;

    const actionNeededReason = fullTextScreeningId
        ? effectiveAIData?.data?.document?.action_needed_reason
        : effectiveAIData?.data?.study?.action_needed_reason;

    // Backend sets this text when the AI model refuses a paper (safety classifier block).
    // Keep in sync with REFUSAL_SUMMARY in slrabstract_agent_executor.py.
    const isAIRefusal = !!actionNeeded && /refused to process this paper/i.test(actionNeededReason || "");

    const PicoTag = ({ text, picoJson }) => {
        const picoType = getPicoTypeForTag(text, picoJson);
        const color = PICO_COLORS[picoType];

        return (
            <span
                className="ev-btn-xsmall ev-bold-reg py-1 px-2 ev-radius-large"
                style={{
                    background: color ? `${color}22` : "#f2f3f8",
                    color: color || "#333",
                }}
            >
                {text}
            </span>
        );
    };

    useEffect(() => {
        if (!abstractId || !slrFabricId) return;

        // Reset selected model when switching to a new abstract
        setSelectedModelIndex(0);

        const fetchAIDetails = async () => {
            setLoadingAbstract(true);
            try {
              const res = await axios.post(
                "/pceapi/slr/abstract-screening-ai/detail",
                {
                  slr_fabric_id: slrFabricId,
                  abstract_screening_id: abstractId,
                  ...activeFilters,
                },
                { headers },
              );

              if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
              }

              if (res.data?.status === "success") {
                setAiData(res.data);

                // If fullTextScreeningId exists, full-text navigation will handle it
                if (
                  !fullTextScreeningId &&
                  res.data?.data?.navigation &&
                  onNavigationUpdate
                ) {
                  onNavigationUpdate({
                    ...res.data.data.navigation,
                    study_id: res.data.data.study?.study_id
                  });
                }
              }
            } catch (err) {
              console.error("Failed to fetch AI abstract details", err);

              if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
              }

              showError(
                err.response?.data?.message ||
                  "Failed to load AI abstract details",
              );
            } finally {
              setLoadingAbstract(false);
            }
        };

        fetchAIDetails();
    }, [abstractId, slrFabricId, fullTextScreeningId, activeFilters]);


    useEffect(() => {
        if (!fullTextScreeningId || !slrFabricId) return;

        const fetchFullTextAIDetails = async () => {
            setLoadingFullText(true);
            try {
              const res = await axios.post(
                "/pceapi/slr/full-text-screening-ai/detail",
                {
                  slr_fabric_id: slrFabricId,
                  full_text_screening_id: fullTextScreeningId,
                  ...activeFilters,
                },
                { headers },
              );

              if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
              }

              if (res.data?.status === "success") {
                setAiFullTextData(res.data);

                // Prefer full-text navigation when present
                if (res.data?.data?.navigation && onNavigationUpdate) {
                  onNavigationUpdate({
                    ...res.data.data.navigation,
                    study_id: res.data.data.document?.study_id
                  });
                }
              }
            } catch (err) {
              console.error("Failed to fetch AI full-text details", err);

              if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
              }

              showError(
                err.response?.data?.message ||
                  "Failed to load AI full-text details",
              );
            } finally {
              setLoadingFullText(false);
            }
        };

        fetchFullTextAIDetails();
    }, [fullTextScreeningId, slrFabricId, activeFilters]);

    useEffect(() => {
        if (actionNeeded) {
            showError(
                isAIRefusal
                    ? "The AI model couldn't process this paper (safety block). Please screen it manually. See the Summary panel on the right for details."
                    : "This paper couldn't be screened due to possible gaps in the project configuration. Review the Summary panel on the right for specifics."
            );
        }
    }, [aiData, aiFullTextData]);

    const literature = aiData
        ? {
            title: aiData.data?.study?.title || "No Title Available",
            abstract: (!aiData.data?.study?.abstract || aiData.data?.study?.abstract === "nan") ? "Not Available" : aiData.data?.study?.abstract,
            author_names: aiData.data?.study?.author_names || "",
            publication_year: aiData.data?.study?.publication_year || "",
            publication_type: aiData.data?.study?.publication_type || null,
            journal_abbr: aiData.data?.study?.journal_abbr || null,
            volume: aiData.data?.study?.volume || null,
            issue: aiData.data?.study?.issue || null,
            pages: aiData.data?.study?.pages || null,
            doi: aiData.data?.study?.doi || null,
            publication_category: aiData.data?.study?.publication_category || null,
            language: aiData.data?.study?.language || [],
            embase_id: aiData.data?.study?.embase_id || null,
            // No "Full text" link in abstract screening — decide on the abstract only.
            full_text_link: null,
            conference_details: aiData.data?.study?.conference_details || null,
            pico_json: pico_json || {},
        }
        : null;

    const fullTextLiterature = aiFullTextData
        ? {
            title: aiFullTextData.data?.document?.title || "No Title Available",
            authors: aiFullTextData.data?.document?.authors || [],
            year: aiFullTextData.data?.document?.year,
            publisher: aiFullTextData.data?.document?.publisher,
            doi: aiFullTextData.data?.document?.doi,
            presigned_url: aiFullTextData.data?.document?.presigned_url,
            pico_json: pico_json || {},
        }
        : null;

    const matchingTags = useMemo(() => {
        const picoJson = literature?.pico_json || pico_json || {};
        const normalized = normalizePicoJson(picoJson);
        const title = (literature?.title || "").toLowerCase();
        const abstract = (literature?.abstract || literature?.description || "").toLowerCase();
        const text = `${title} ${abstract}`;
    
        if (!text.trim()) return [];
    
        const allKeywords = Object.values(normalized)
          .filter(Array.isArray)
          .flat()
          .map((k) => (k == null ? "" : String(k)).trim())
          .filter(Boolean);
    
        return [...new Set(allKeywords.filter((kw) => text.includes(kw.toLowerCase())))];
      }, [literature, pico_json]);

    // Loading State
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!effectiveAIData) {
        return <div className="p-3">No AI data available.</div>;
    }

    const hasMultipleModels = effectiveAIData?.data?.ai_decisions?.length > 1;
    const isDuplicateExclusion = effectiveAIData?.data?.ai_decisions?.length > 0 &&
      effectiveAIData.data.ai_decisions.every((d) => (d.summary || "").toLowerCase().includes("duplicate entry"));

    return (
      <div className="row g-0 overflow-hidden">
        {/* ================= LEFT: ABSTRACT ================= */}
        <div className="col-md-8 pe-2">
          {fullTextScreeningId ? (
            <ScreeningPDFPanel literature={fullTextLiterature} />
          ) : (
            <ScreeningAbstractPanel literature={literature} />
          )}
        </div>

        {/* ================= RIGHT: AI DECISION ================= */}
        <div className="ev-card-2 col-md-4 vh-75 scrollable-element p-2 ev-radius-small">
          {/* Header */}
          <div className="ev-card-3 p-2 mb-2 ev-radius-xsmall text-center">
            <p className="ev-bold-med gradient-font9 ev-fs-14 mb-0">
              AI Decision
            </p>
          </div>

          {/* Decisions - Multiple Models */}
          <p className="ev-small-p-blue ev-bold-med mb-1">Decisions</p>
          {actionNeeded ? (
            <div className="d-flex justify-content-between align-items-center p-2 mb-1 ev-radius-xsmall ev-card-8">
              <span className="ev-reg-p-dark ev-bold-med ps-2">
                {(() => {
                  const m = effectiveAIData?.data?.ai_decisions?.[0]?.model;
                  return m === "textmodel" ? "claude-sonnet-4-6" : (m || "Model 1");
                })()} 
              </span>
              <span className="ev-alert-card-warning ev-radius-xsmall ev-btn-small px-2 d-flex align-items-center gap-1 me-2">
                <i className="bi bi-exclamation-triangle-fill"></i> 
              </span>
            </div>
          ) : isDuplicateExclusion ? (
            <div className="d-flex justify-content-between align-items-center p-2 mb-1 ev-radius-xsmall ev-card-8">
              <span className="ev-alert-card-warn p-1 ev-radius-xsmall px-3 ev-fs-11">Duplicate</span>
              <div className="ev-decision-card excluded">
                <span className="ev-decision-icon excluded">
                  <i className="bi bi-x-lg" />
                </span>
                Exclude
              </div>
            </div>
          ) : (
            effectiveAIData?.data?.ai_decisions?.map((aiDecision, index) => {
              const isIncluded = aiDecision.decision === "INCLUDED";
            const isSelected = selectedModelIndex === index;

            return (
              <div
                key={index}
                className={`d-flex justify-content-between align-items-center p-2 mb-1 ev-radius-xsmall ${isSelected ? "ev-card-3" : "ev-card-8"}`}
              >
                <span className="ev-reg-p-dark ev-bold-med ps-2">
                  {aiDecision.model === "textmodel" ? "claude-sonnet-4-6" : aiDecision.model}
                </span>
                <div className="d-flex gap-2 align-items-center">
                {hasMultipleModels && (
                  <button
                    className={`ev-btn-small ev-bold-med ${isSelected ? "ev-btn-blue-fill-round" : "ev-btn-blue-outline-round"}`}
                    onClick={() => setSelectedModelIndex(index)}
                  >
                    {isSelected ? "Showing" : "Show"}
                  </button>
                  )}

                <div
                    className={`ev-decision-card ${aiDecision.decision ? (isIncluded ? "included" : "excluded") : ""}`}
                    style={!aiDecision.decision ? { visibility: "hidden" } : {}}
                  >
                    <span className={`ev-decision-icon ${isIncluded ? "included" : "excluded"}`}>
                      <i className={`bi ${isIncluded ? "bi-check-lg" : "bi-x-lg"}`} />
                    </span>
                    {isIncluded ? "Include" : "Exclude"}
                  </div>
                </div>
              </div>
            );
          })
        )}

      {!actionNeeded && (
        <>
          <hr />

          <p className="ev-small-p-blue ev-bold-med mb-1">Reasons</p>
          <div className="mb-1">
            {(() => {
              const selectedDecision =
                effectiveAIData?.data?.ai_decisions?.[selectedModelIndex];
              if (!selectedDecision?.reasons?.length) {
                return (
                  <p className="ev-xsmall-p-dark ps-2">No reasons available.</p>
                );
              }
              return selectedDecision.reasons.map((item, idx) => (
                <ReasonRow
                  key={`${selectedModelIndex}-${idx}`}
                  reason={{
                    type: item?.criteria?.toLowerCase().includes("inclusion") ? "include" : "exclude",
                    label: item?.reason_code || item?.reason,
                  }}
                />
              ));
            })()}
          </div>
        </>
      )}

          <hr />

          <p className="ev-small-p-blue ev-bold-med mb-1">Summary</p>

          <textarea
            readOnly
            rows={3}
            className="formField3 ev-card-6 ev-small-p-dark ev-radius-xsmall w-100 p-2"
            value={
              actionNeeded
                ? (actionNeededReason || "—")
                : (effectiveAIData?.data?.ai_decisions?.[selectedModelIndex]?.summary || "—")
            }
            style={{ resize: "vertical" }}
          />

          {!fullTextScreeningId && (
            <>
              <hr />
              <p className="ev-small-p-blue ev-bold-med mb-1">Tags</p>
              <div className="d-flex flex-wrap gap-1">
                {matchingTags.length > 0 ? (
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {matchingTags.map((tag, i) => (
                      <PicoTag
                        key={i}
                        text={tag}
                        picoJson={literature?.pico_json}
                      />
                    ))}
                  </div>
                ) : (
                  <span className="ev-xsmall-p-dark">
                    No matching tags found for this paper
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        {isErrorPopupVisible && (
          <ErrorPopup message={popupErrorMessage} onClose={closeErrorPopup} />
        )}
      </div>
    );
};

/* ================= SUB COMPONENTS ================= */
const ReasonRow = ({ reason }) => {
    const isInclude = reason.type === "include";

    return (
      <div className="d-flex align-items-center gap-1 mb-1">
        <div
          className={`d-flex align-items-center gap-1 p-1 ev-radius-xsmall ev-bold-med
          ${isInclude ? "slr-criteria-incl" : "slr-criteria-excl"}`}
          style={{ minWidth: 90, justifyContent: "center" }}
        >
          <span
            className="d-flex align-items-center justify-content-center ev-radius-large ev-bold-large"
            style={{
              width: 16,
              height: 16,
              background: isInclude ? "#4CAF50" : "#E53935",
              color: "#fff",
              fontSize: 11,
              lineHeight: "16px",
            }}
          >
            {isInclude ? "✓" : "✕"}
          </span>

          <span className="ev-reg-p-dark">{isInclude ? "Include" : "Exclude"}</span>
        </div>

        <textarea
          rows={1}
          readOnly
          value={reason.label || ""}
          className="formField3 flex-grow-1 ev-card-8 p-1 ev-radius-xsmall ev-small-p-dark-blue ev-bold-reg"
        />
        
      </div>
    );
};

export default AIResponsePanel;